/**
 * ClerkAccessDrawer — the barrister's steering wheel for clerk access.
 *
 * Per-clerk global capabilities as a 3-way control (Full / Limited / No Access),
 * one-click presets, and a list of per-matter exceptions. Backed by the
 * /me/clerk-access API; every change is audited server-side.
 * See docs/04-ENGINEERING/CAPABILITIES/access-control.md.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, CircleNotch, Trash, ClockCountdown } from '@phosphor-icons/react';
import {
  fetchClerkAccess,
  fetchClerkPermissionHistory,
  saveClerkCapabilities,
  clearClerkMatterOverride,
  type CapabilityState,
  type ClerkOverride,
} from '@/lib/clerkAccessApi';
import { ScrollLock } from '@/components/shared/ScrollLock';

// ─── Permission history ──────────────────────────────────────
// Ported as-is from the retired DelegationSettingsPage (removed in 33865b1, when clerk
// management consolidated onto My Clerks and the history view was dropped by accident).
// Reads GET /me/clerk-access/clerks/:clerkId/log — the barrister's audit trail of what they
// granted, restricted or excepted for this clerk.

function humanizeAction(action: string): string {
  switch (action) {
    case 'CHANGE_PERMISSION': return 'Permissions updated';
    case 'GRANT_PERMISSION': return 'Permission granted';
    case 'REVOKE_PERMISSION': return 'Permission revoked';
    case 'MATTER_OVERRIDE_SET': return 'Matter exception set';
    case 'MATTER_OVERRIDE_CLEARED': return 'Matter exception removed';
    case 'MAKE_PRIVATE': return 'Matter made private';
    case 'REMOVE_PRIVATE': return 'Matter privacy removed';
    default: return action.replace(/_/g, ' ').toLowerCase();
  }
}

function PermissionHistory({ clerkId }: { clerkId: string }) {
  const { data: logs = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['clerk-permission-history', clerkId],
    queryFn: () => fetchClerkPermissionHistory(clerkId),
    staleTime: 0,
  });

  if (isLoading) {
    return (
      <div className="flex h-10 items-center justify-center">
        <CircleNotch size={14} className="animate-spin text-muted-foreground/40" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-[11px] text-red-600 dark:text-red-400">
        Could not load the history.{' '}
        <button type="button" onClick={() => void refetch()} className="underline">Try again</button>
      </p>
    );
  }

  if (logs.length === 0) {
    return <p className="text-[11px] text-muted-foreground">No changes recorded yet.</p>;
  }

  return (
    <div className="space-y-1.5">
      {logs.slice(0, 8).map((entry) => (
        <div key={entry.id} className="rounded border border-border/50 bg-muted/20 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium text-foreground">{humanizeAction(entry.action)}</span>
            <span className="text-[10px] text-muted-foreground">
              {new Date(entry.createdAt).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>
          {entry.capability && (
            <div className="mt-0.5 text-[10px] capitalize text-muted-foreground">{entry.capability}</div>
          )}
        </div>
      ))}
    </div>
  );
}

const CAPABILITIES: { key: string; label: string; hint?: string }[] = [
  { key: 'matters', label: 'Matters' },
  { key: 'documents', label: 'Documents' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'emails', label: 'Emails' },
  { key: 'fees', label: 'Fees', hint: 'Invoices & fee notes' },
  { key: 'reports', label: 'Reports', hint: 'Operational — follows matter access' },
  { key: 'financial_reports', label: 'Financial Reports', hint: 'Revenue, aged debt — global only' },
];

const STATES: { value: CapabilityState; label: string }[] = [
  { value: 'ALLOW', label: 'Full Access' },
  { value: 'RESTRICTED', label: 'Limited' },
  { value: 'FORBIDDEN', label: 'No Access' },
];

function stateFor(caps: Record<string, CapabilityState>, key: string): CapabilityState {
  return caps[key] ?? 'RESTRICTED';
}

export function ClerkAccessDrawer({
  member,
  onClose,
  onDone,
}: {
  member: { userId: string; firstName: string; lastName: string };
  onClose: () => void;
  onDone: () => void;
}) {
  const [caps, setCaps] = useState<Record<string, CapabilityState>>({});
  const [overrides, setOverrides] = useState<ClerkOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    let active = true;
    fetchClerkAccess(member.userId)
      .then((data) => {
        if (!active) return;
        setCaps(data.capabilities ?? {});
        setOverrides(data.overrides ?? []);
      })
      .catch(() => toast.error('Could not load clerk access'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [member.userId]);

  const setCap = (key: string, value: CapabilityState) => setCaps((c) => ({ ...c, [key]: value }));
  const applyAll = (value: CapabilityState) =>
    setCaps(Object.fromEntries(CAPABILITIES.map((c) => [c.key, value])));

  const save = async () => {
    setSaving(true);
    try {
      const full = Object.fromEntries(CAPABILITIES.map((c) => [c.key, stateFor(caps, c.key)]));
      await saveClerkCapabilities(member.userId, full);
      void qc.invalidateQueries({ queryKey: ['clerk-permission-history', member.userId] });
      toast.success('Access updated');
      onDone();
    } catch {
      toast.error('Failed to save access');
    } finally {
      setSaving(false);
    }
  };

  const removeOverride = async (o: ClerkOverride) => {
    try {
      await clearClerkMatterOverride(member.userId, o.matterId, o.capability);
      setOverrides((list) => list.filter((x) => !(x.matterId === o.matterId && x.capability === o.capability)));
      void qc.invalidateQueries({ queryKey: ['clerk-permission-history', member.userId] });
      toast.success('Exception removed');
    } catch {
      toast.error('Failed to remove exception');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <ScrollLock />
      <div
        className="flex h-full w-full max-w-md flex-col bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-sm font-bold text-foreground">Access for {member.firstName} {member.lastName}</h3>
            <p className="text-[11px] text-muted-foreground">Default is no access — grant only what this clerk needs.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center"><CircleNotch size={24} className="animate-spin text-muted-foreground/50" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="mb-4 flex gap-2">
              <button onClick={() => applyAll('ALLOW')} className="rounded-md border px-2.5 py-1 text-[11px] font-semibold hover:bg-muted">Trust everything</button>
              <button onClick={() => applyAll('RESTRICTED')} className="rounded-md border px-2.5 py-1 text-[11px] font-semibold hover:bg-muted">No access</button>
            </div>

            <div className="space-y-3">
              {CAPABILITIES.map((cap) => {
                const current = stateFor(caps, cap.key);
                return (
                  <div key={cap.key} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-foreground">{cap.label}</div>
                      {cap.hint && <div className="text-[10px] text-muted-foreground">{cap.hint}</div>}
                    </div>
                    <div className="flex shrink-0 overflow-hidden rounded-md border">
                      {STATES.map((s) => (
                        <button
                          key={s.value}
                          onClick={() => setCap(cap.key, s.value)}
                          className={
                            'px-2 py-1 text-[10px] font-semibold transition ' +
                            (current === s.value
                              ? s.value === 'ALLOW'
                                ? 'bg-emerald-500 text-white'
                                : s.value === 'FORBIDDEN'
                                  ? 'bg-red-500 text-white'
                                  : 'bg-muted-foreground/80 text-white'
                              : 'bg-background text-muted-foreground hover:bg-muted')
                          }
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6">
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Per-matter exceptions</h4>
              {overrides.length === 0 ? (
                <p className="mt-1 text-[11px] text-muted-foreground">None. Open a matter to allow or deny a single case.</p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {overrides.map((o) => (
                    <li key={`${o.matterId}:${o.capability}`} className="flex items-center justify-between rounded-md border px-2.5 py-1.5 text-[11px]">
                      <span className="truncate">
                        <span className="font-medium">{o.caseName ?? o.matterId.slice(0, 8)}</span>
                        {' — '}{o.capability}{' '}
                        <span className={o.effect === 'ALLOW' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>{o.effect === 'ALLOW' ? 'Allowed' : 'Denied'}</span>
                      </span>
                      <button onClick={() => removeOverride(o)} className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"><Trash size={13} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowHistory((x) => !x)}
                className="flex items-center gap-1.5 text-[11px] text-muted-foreground transition hover:text-foreground"
              >
                <ClockCountdown size={12} />
                {showHistory ? 'Hide history' : 'View permission history'}
              </button>
              {showHistory && (
                <div className="mt-3">
                  <PermissionHistory clerkId={member.userId} />
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <button onClick={onClose} className="rounded-md border px-3 py-1.5 text-xs font-semibold hover:bg-muted">Cancel</button>
          <button onClick={save} disabled={saving || loading} className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-50">
            {saving ? 'Saving…' : 'Save access'}
          </button>
        </div>
      </div>
    </div>
  );
}
